import { getInverseOperator } from "@editor/items/condition/conditionFunctions";
import { defineItemMigration } from "@editor/items/itemStoreMigration";

import type { ConditionItem } from "../ConditionItem";
import type { StoreItem } from "@editor/items/StoreItem";

export default defineItemMigration<ConditionItem, LegacyConditionItem>(
  (itemStore, oldCondition, newCondition, functions) => {
    // If the condition is basically empty, do nothing:
    if (!("activatorType" in oldCondition)) {
      return;
    }

    // If the condition is already in the NEW format, do nothing:
    if ("activatorProperty" in oldCondition) {
      return;
    }

    if (oldCondition.activatorType === "always") {
      // There won't be any "always" conditions in the future.
      // We have to modify the src object to "always" activate it.
      itemStore.order = itemStore.order.filter((id) => id !== oldCondition.id);
      if (oldCondition.id != null) {
        delete itemStore.items[oldCondition.id];
      }
      return;
    }

    // Translate the operator to the new format:
    if (oldCondition.operator === "larger") {
      newCondition.operator = ">";
    } else if (oldCondition.operator === "smaller") {
      newCondition.operator = "<";
    } else if (oldCondition.operator === "equals") {
      newCondition.operator = "=";
    }

    newCondition.comparedValue = oldCondition.value;

    // Fill in the activatorProperty:
    if (
      oldCondition.activatorType === "element" ||
      oldCondition.activatorType === "resource" ||
      oldCondition.activatorType === "index"
    ) {
      newCondition.comparedValueType = "number";
      newCondition.activatorProperty = "value";
    } else if (
      oldCondition.activatorType === "incident" ||
      oldCondition.activatorType === "measure"
    ) {
      // define search range: all simuation
      newCondition.activatorStartType = "startRound";
      newCondition.activatorEndType = "currentRound";

      newCondition.activatorProperty = "activationCount";
      if (oldCondition.operator == "larger") {
        if (oldCondition.value == 1) {
          newCondition.operator = "?";
        } else {
          newCondition.operator = ">=";
        }
      } else {
        if (oldCondition.value == 0) {
          newCondition.operator = "!";
        } else {
          newCondition.operator = "<=";
        }
      }
    } else if (
      oldCondition.activatorType === "round" ||
      oldCondition.activatorType === "roundRange"
    ) {
      newCondition.activatorProperty = null;
      newCondition.operator = "?";
    }

    if (oldCondition.activatorType === "roundRange") {
      newCondition.comparedValue = null;
    }

    if (oldCondition.activatorType == "measureActivation") {
      newCondition.activatorType = "measure";
    } else if (oldCondition.activatorType == "incidentActivation") {
      newCondition.activatorType = "incident";
    } else {
      newCondition.activatorType = oldCondition.activatorType;
    }

    if (
      oldCondition.activatorType == "measureActivation" ||
      oldCondition.activatorType == "incidentActivation"
    ) {
      newCondition.activatorProperty = "activationCount";
      newCondition.activatorStartType = "relativeRound";
      newCondition.activatorStart = Math.max(oldCondition.value - 1, 0);
      newCondition.activatorEndType = "relativeRound";
      newCondition.activatorEnd = 0;
      if (oldCondition.operator == "larger") {
        newCondition.operator = ">=";
      } else if (oldCondition.operator == "smaller") {
        newCondition.operator = "<=";
      } else if (oldCondition.operator == "equals") {
        newCondition.operator = "=";

        newCondition.activatorEnd = newCondition.activatorStart;
      }
    }

    if (oldCondition.activatorType === "roundRange") {
      newCondition.comparedValue = null;

      newCondition.activatorStart = oldCondition.value - 1;
      newCondition.activatorStartType = "absoluteRound";
      newCondition.activatorEnd = oldCondition.valueB - 1;
      newCondition.activatorEndType = "absoluteRound";
    }

    if (oldCondition.activatorType === "round") {
      newCondition.operator = "=";
      // Adjust the round to the new format:
      newCondition.comparedValue = oldCondition.value - 1;
      newCondition.comparedValueType = "absoluteRound";
    }

    // HANDLE ENTRY TYPE:
    // singular conditions will be split into two conditions:
    // - one condition that checks if the activator is active
    // - one condition that checks if the activator was NOT active in the last round
    // So: create the second condition:
    if (oldCondition.entryType == "singular") {
      const inverseOperator = getInverseOperator(newCondition.operator);

      const extraCondition = functions.create({
        srcType: oldCondition.srcType,
        srcId: oldCondition.srcId,

        activatorType: newCondition.activatorType,
        activatorId: newCondition.activatorId,
        activatorProperty: newCondition.activatorProperty,

        activatorStartType: "relativeRound",
        activatorStart: 1,

        operator: inverseOperator,

        comparedValue: newCondition.comparedValue
      });
      console.log("add extra condition for singular condition", extraCondition);
    }
  }
);

interface LegacyConditionItem extends StoreItem {
  id: number;
  type: "condition";

  activatorType:
    | "element"
    | "resource"
    | "index"
    | "measure"
    | "incident"
    | "round"
    | "roundRange"
    | "always"
    | "measureActivation"
    | "incidentActivation";
  activatorId: number | null;
  operator: "larger" | "smaller" | "equals";
  entryType: "constant" | "singular"; // constant: always, singular: on status change
  value: number;
  valueB: number;
  doc: string;

  srcType: "scenarioEnd" | "measure" | "incidentTrigger";
  srcId: number | null;

  order: number;
}
